import { useEffect, useState } from 'react';
import { Button, Input } from '../design-system';
import { sanitizeOtp, isCompleteOtp, OTP_LENGTH } from '../lib/otpInput';
import './TotpSetupSteps.css';

// The scan-and-confirm pair, shared by the two places someone enrols:
// the login screen (when their role requires 2FA and they have none) and the
// Security panel (when they turn it on themselves). One component, because the
// steps and the warnings must read identically in both — the recovery-code
// screen in particular is the only time those codes are ever shown.

/** Step 1: show the QR and take the code that proves it was scanned. */
export function TotpScanStep({ setup, onConfirm, loading, error, onCancel }) {
  const [code, setCode] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  return (
    <form
      className="totp-step"
      onSubmit={(e) => {
        e.preventDefault();
        onConfirm(code);
      }}
    >
      <ol className="totp-instructions">
        <li>Open your authenticator app — Google Authenticator, Microsoft Authenticator, 1Password or Authy.</li>
        <li>Scan this code.</li>
        <li>Type the 6-digit number it shows.</li>
      </ol>

      {setup?.qrDataUrl && (
        <div className="totp-qr">
          <img src={setup.qrDataUrl} alt="Authenticator setup QR code" />
        </div>
      )}

      {/* For a desktop password manager, or a phone that cannot scan. */}
      <div className="totp-manual">
        <button type="button" className="totp-link" onClick={() => setShowSecret((v) => !v)}>
          {showSecret ? 'Hide setup key' : "Can't scan? Enter a key instead"}
        </button>
        {showSecret && <code className="totp-secret">{setup?.secret}</code>}
      </div>

      {error && (
        <div className="totp-error" role="alert">
          {error}
        </div>
      )}

      <Input
        label="6-digit code"
        id="totp-confirm-code"
        inputMode="numeric"
        autoComplete="one-time-code"
        placeholder="123456"
        // Enrolment only ever takes a code from the app — there are no recovery
        // codes yet at this point — so this is the strict six-digit filter.
        maxLength={OTP_LENGTH}
        value={code}
        onChange={(e) => setCode(sanitizeOtp(e.target.value))}
        disabled={loading}
        autoFocus
      />

      <Button
        type="submit"
        variant="primary"
        size="lg"
        loading={loading}
        className="totp-submit"
        disabled={!isCompleteOtp(code)}
      >
        Turn on two-factor authentication
      </Button>
      {onCancel && (
        <button type="button" className="totp-link totp-cancel" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      )}
    </form>
  );
}

/**
 * Step 2: the recovery codes.
 *
 * Shown exactly once — they are stored hashed, so nothing can ever retrieve
 * them again, only replace them. The continue button stays disabled until the
 * user ticks that they've saved them, because "I'll do it later" is how the
 * lost-phone lockout happens.
 */
export function TotpRecoveryCodesStep({ codes, onDone, doneLabel = 'Continue' }) {
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
    } catch {
      // Clipboard is blocked on insecure origins and in some browsers; the
      // codes are on screen anyway, so this is a convenience, not the path.
    }
  };

  return (
    <div className="totp-step">
      <div className="totp-callout">
        <strong>Save these recovery codes now.</strong> Each one signs you in once if you lose your
        phone. This is the only time they are shown — we store them hashed and cannot show them
        again.
      </div>

      <ul className="totp-codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>

      <div className="totp-code-actions">
        <Button type="button" variant="secondary" size="sm" onClick={copy}>
          {copied ? 'Copied' : 'Copy codes'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => window.print()}
        >
          Print
        </Button>
      </div>

      <label className="totp-confirm-saved">
        <input type="checkbox" checked={saved} onChange={(e) => setSaved(e.target.checked)} />
        <span>I have saved these codes somewhere safe</span>
      </label>

      <Button
        type="button"
        variant="primary"
        size="lg"
        className="totp-submit"
        disabled={!saved}
        onClick={onDone}
      >
        {doneLabel}
      </Button>
    </div>
  );
}
